/**
 * The three cut points of components.md §1, read from the token build.
 *
 * A hook and not a resize listener: `matchMedia` fires only when a boundary is
 * crossed, where a `resize` handler fires on every pixel of a drag and would run
 * inside the same frame budget as the book.
 *
 * The values come from `design-system/tokens.json`, the same file the Tailwind
 * bridge emits `--breakpoint-*` from, so `lg:` in a class name and `lg` here can
 * never mean two different widths. `breakpoints.test.ts` asserts that.
 */

import { useSyncExternalStore } from 'react'
import tokens from '../../../design-system/tokens.json'

export type Breakpoint = 'base' | 'md' | 'lg' | 'xl'

/** `{ md: 768, lg: 1120, xl: 1440 }` — parsed from the generated tokens. */
export const BREAKPOINT_PX: Record<Exclude<Breakpoint, 'base'>, number> = {
  md: Number.parseInt(tokens.breakpoints.md, 10),
  lg: Number.parseInt(tokens.breakpoints.lg, 10),
  xl: Number.parseInt(tokens.breakpoints.xl, 10),
}

const ORDER: Breakpoint[] = ['base', 'md', 'lg', 'xl']

function current(): Breakpoint {
  const width = globalThis.innerWidth ?? 0
  if (width >= BREAKPOINT_PX.xl) return 'xl'
  if (width >= BREAKPOINT_PX.lg) return 'lg'
  if (width >= BREAKPOINT_PX.md) return 'md'
  return 'base'
}

function subscribe(onChange: () => void): () => void {
  if (typeof globalThis.matchMedia !== 'function') return () => {}
  const queries = Object.values(BREAKPOINT_PX).map((px) =>
    globalThis.matchMedia(`(min-width: ${px}px)`),
  )
  for (const q of queries) q.addEventListener?.('change', onChange)
  return () => {
    for (const q of queries) q.removeEventListener?.('change', onChange)
  }
}

/** The active breakpoint. `base` is <768px. */
export function useBreakpoint(): Breakpoint {
  return useSyncExternalStore(subscribe, current, () => 'xl' as const)
}

/** True at or above `min`. `useAtLeast('lg')` is "the ticket has its own column". */
export function useAtLeast(min: Breakpoint): boolean {
  const active = useBreakpoint()
  return ORDER.indexOf(active) >= ORDER.indexOf(min)
}
